import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Spinner,
  IconButton,
  Tooltip,
  ButtonGroup,
  Button,
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
} from '@material-tailwind/react';
import {
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  ComputerDesktopIcon,
  EyeIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

const DEVICE_SIZES = {
  mobile: { width: 375, label: 'Mobile' },
  tablet: { width: 768, label: 'Tablet' },
  desktop: { width: '100%', label: 'Desktop' },
};

// Map sidebar section IDs to HTML element IDs in the report
const SECTION_ID_MAP = {
  cover: 'cover',
  tableOfContents: 'table-of-contents',
  about: 'about',
  marketTrends: 'market-trends',
  onMarketComps: 'on-market-comps',
  recentlySoldComps: 'recently-sold-comps',
  summaryPricing: 'summary-pricing',
  strategy: 'strategy',
  assumptions: 'assumptions',
  listingPerformance: 'listing-performance',
  valuationHistory: 'valuation-history',
  recommendations: 'recommendations',
};

function PreviewPane({
  shareableContent,
  clientContent,
  isLoadingShareable,
  isLoadingClient,
  isApplyingExclusions,
  errorShareable,
  errorClient,
  onRefreshShareable,
  onLoadClientPreview,
  selectedSection,
}) {
  const [activeTab, setActiveTab] = useState('preparer');
  const [deviceSize, setDeviceSize] = useState('desktop');
  const preparerIframeRef = useRef(null);
  const clientIframeRef = useRef(null);

  // Scroll iframe to the selected section
  const scrollToSelected = useCallback(() => {
    // Custom sections use their id directly (the report wraps them in
    // <div id="section-custom_..." data-section="custom_...">)
    const htmlId = SECTION_ID_MAP[selectedSection]
      || (selectedSection?.startsWith('custom_') ? selectedSection : null);
    if (!htmlId) return;
    const iframe = activeTab === 'preparer' ? preparerIframeRef.current : clientIframeRef.current;
    if (!iframe) return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;

      const candidates = [htmlId, selectedSection, htmlId.replace(/-/g, '_'), `section-${selectedSection}`];
      for (const id of candidates) {
        const el = doc.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }

      const byAttr = doc.querySelector(`[data-section="${htmlId}"], [data-section="${selectedSection}"]`);
      if (byAttr) {
        byAttr.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch { /* iframe cross-origin or not ready */ }
  }, [selectedSection, activeTab]);

  // Scroll when section selection changes
  useEffect(() => {
    scrollToSelected();
    // Also retry after a short delay in case iframe is still loading
    const timer = setTimeout(scrollToSelected, 500);
    return () => clearTimeout(timer);
  }, [selectedSection, scrollToSelected]);

  // Listen for postMessage from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'excludePoints') {
        window.dispatchEvent(
          new CustomEvent('excludePoints', { detail: { excludedIds: event.data.excludedIds || [] } })
        );
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const getIframeWidth = () => {
    if (deviceSize === 'desktop') return '100%';
    return DEVICE_SIZES[deviceSize].width;
  };

  const currentRefresh = activeTab === 'preparer' ? onRefreshShareable : onLoadClientPreview;

  const renderPreview = (content, isLoading, error, iframeRef) => (
    <div className="h-full border rounded-lg overflow-hidden relative bg-gray-100 flex items-center justify-center">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-30">
          <Spinner className="h-8 w-8" />
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50 z-30">
          <Typography color="red" variant="small">Failed to load preview: {error}</Typography>
        </div>
      )}
      {isApplyingExclusions && (
        <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center z-20">
          <Spinner className="h-8 w-8 text-amber-500 mb-3" />
          <Typography variant="small" className="font-medium text-amber-700">Updating analysis...</Typography>
        </div>
      )}
      <div
        className="h-full bg-white overflow-hidden"
        style={{
          width: getIframeWidth(),
          maxWidth: '100%',
          boxShadow: deviceSize !== 'desktop' ? '0 0 20px rgba(0,0,0,0.1)' : 'none',
        }}
      >
        {content ? (
          <iframe
            ref={iframeRef}
            title="Preview"
            className="w-full h-full border-0"
            srcDoc={content}
            sandbox="allow-same-origin allow-scripts allow-downloads"
            onLoad={scrollToSelected}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Typography color="gray" variant="small">Loading preview...</Typography>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Card className="h-full flex flex-col">
      <CardBody className="flex-1 flex flex-col p-4 overflow-hidden">
        <Tabs value={activeTab} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <TabsHeader className="bg-gray-100 p-1">
              <Tab value="preparer" onClick={() => setActiveTab('preparer')} className="flex items-center gap-1.5 px-3 py-1.5">
                <UserIcon className="h-4 w-4" />
                <span className="text-xs font-medium">Preparer</span>
              </Tab>
              <Tab value="client" onClick={() => setActiveTab('client')} className="flex items-center gap-1.5 px-3 py-1.5">
                <EyeIcon className="h-4 w-4" />
                <span className="text-xs font-medium">Client</span>
              </Tab>
            </TabsHeader>

            <div className="flex items-center gap-2">
              <ButtonGroup size="sm" variant="outlined" className="mr-2">
                <Tooltip content="Mobile">
                  <Button className={`px-2 ${deviceSize === 'mobile' ? 'bg-gray-100' : ''}`} onClick={() => setDeviceSize('mobile')}>
                    <DevicePhoneMobileIcon className="h-4 w-4" />
                  </Button>
                </Tooltip>
                <Tooltip content="Tablet">
                  <Button className={`px-2 ${deviceSize === 'tablet' ? 'bg-gray-100' : ''}`} onClick={() => setDeviceSize('tablet')}>
                    <DeviceTabletIcon className="h-4 w-4" />
                  </Button>
                </Tooltip>
                <Tooltip content="Desktop">
                  <Button className={`px-2 ${deviceSize === 'desktop' ? 'bg-gray-100' : ''}`} onClick={() => setDeviceSize('desktop')}>
                    <ComputerDesktopIcon className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </ButtonGroup>
              {currentRefresh && (
                <Tooltip content="Refresh preview">
                  <IconButton variant="text" size="sm" onClick={currentRefresh}>
                    <ArrowPathIcon className="h-4 w-4" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>

          <TabsBody className="flex-1 overflow-hidden">
            <TabPanel value="preparer" className="h-full p-0">
              {renderPreview(shareableContent, isLoadingShareable, errorShareable, preparerIframeRef)}
            </TabPanel>
            <TabPanel value="client" className="h-full p-0">
              {renderPreview(clientContent, isLoadingClient, errorClient, clientIframeRef)}
            </TabPanel>
          </TabsBody>
        </Tabs>
      </CardBody>
    </Card>
  );
}

export default PreviewPane;
